import { Injectable } from '@angular/core';
import { PaymentDocumentHelperService } from './payment-document-helper.service';
import {
  ModifiedDocumentMeta,
  PaymentPreviewV1Response,
  SessionDocumentInfo,
  CreditAppliedActionPayload,
  ValidateCreditMemoErrors,
} from '../models';
import { getEditableAmountErrorMessage, roundTo2Decimals } from '../helpers';
import { MessageService } from '@arc/aureus';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class CreditMemoHelperService {
  constructor(
    private paymentDocumentService: PaymentDocumentHelperService,
    private messageService: MessageService,
    private translationService: TranslateService,
  ) {}

  applyCreditMemos(
    sessionDocuments: SessionDocumentInfo[],
    paymentPreview: PaymentPreviewV1Response,
    creditMemoSubtotal: number,
    subTotal: number,
    isZeroDollarEnabled: boolean,
    modifiedDocumentBurns: ModifiedDocumentMeta[],
  ) {
    const payload =
      paymentPreview?.payments.length > 1
        ? this.calculateZeroDollarPaymentsUsingCreditMemos(
            sessionDocuments,
            paymentPreview,
            isZeroDollarEnabled,
            modifiedDocumentBurns,
          )
        : this.calculateZeroDollarPaymentUsingCreditMemos(
            creditMemoSubtotal,
            subTotal,
            sessionDocuments,
            isZeroDollarEnabled,
            modifiedDocumentBurns,
          );

    return payload;
  }

  validateCreditMemoEditedPayableAmount(
    editedDocument: SessionDocumentInfo,
    paymentPreview: PaymentPreviewV1Response,
    selectedDocuments: SessionDocumentInfo[],
    subTotal: number,
    creditMemoSubTotal: number,
    isZeroDollarEnabled: boolean,
  ): ValidateCreditMemoErrors {
    let splitDocuments: SessionDocumentInfo[];
    let splitSubTotal: number;
    let splitCreditTotal: number;

    if (paymentPreview?.payments.length > 1) {
      splitDocuments = selectedDocuments.filter((document) =>
        paymentPreview.payments
          .find((p) =>
            p.payment.payables.some((d) => d.burn === editedDocument.urn),
          )
          .payment.payables.map((p) => p.burn)
          .includes(document.urn),
      );
      splitSubTotal = this.paymentDocumentService.getSubTotal(splitDocuments);
      splitCreditTotal =
        this.paymentDocumentService.getCreditMemoSubTotal(splitDocuments);
    } else {
      splitDocuments = selectedDocuments;
      splitSubTotal = subTotal;
      splitCreditTotal = creditMemoSubTotal;
    }

    const errors = this.validateCreditMemoPayableAmount(
      editedDocument,
      splitDocuments,
      splitSubTotal,
      splitCreditTotal,
      isZeroDollarEnabled,
    );

    if (
      errors.isCreditUsedForOverPayment ||
      errors.isNegativePayment ||
      errors.isZeroDollarPayment
    ) {
      return errors;
    }

    return null;
  }

  public displayCreditMemoError(
    errors: ValidateCreditMemoErrors,
    hasDocumentsFromMultipleAccounts: boolean,
    isZeroDollarPaymentEnabled: boolean,
  ) {
    const errorKey = this.getCreditMemoErrorMessage(
      errors,
      hasDocumentsFromMultipleAccounts,
      isZeroDollarPaymentEnabled,
    );

    // this is a TEMPORARY fix to avoid show multiple messages because we are calling multiple times the preview endpoint.
    // We are using this to show the message and dismiss others with the same text
    // see: https://aureus.aws-dev.billtrust.com/components/message
    this.messageService.open(
      this.translationService.instant(errorKey) as string,
      null,
      {
        severity: 'danger',
        channel: 'creditMemoError',
        duration: 0,
        dismissOnNavigate: true,
        dismissOthers: true,
      },
    );
  }

  private getCreditMemoErrorMessage(
    errors: ValidateCreditMemoErrors,
    hasDocumentsFromMultipleAccounts: boolean,
    isZeroDollarPaymentEnabled: boolean,
  ) {
    if (errors.isNegativePayment || errors.isZeroDollarPayment) {
      const errorKey = getEditableAmountErrorMessage(
        hasDocumentsFromMultipleAccounts,
        isZeroDollarPaymentEnabled,
      );
      return errorKey;
    }
    if (errors.isCreditUsedForOverPayment) {
      return 'ARC_PAYMENT_EDIT_PAYABLE_CREDIT_USED_FOR_OVER_PAY_ERROR';
    }

    return null;
  }

  private validateCreditMemoPayableAmount(
    editedDocument: SessionDocumentInfo,
    originalDocuments: SessionDocumentInfo[],
    subTotal: number,
    creditMemoSubTotal: number,
    isZeroDollarPaymentEnabled: boolean,
  ): ValidateCreditMemoErrors {
    // find original document payable amount being edited
    const originalPayableAmount: number = originalDocuments.find(
      (x) => x.urn === editedDocument.urn,
    ).payableAmount;

    const originalCreditMemoSubTotal: number = creditMemoSubTotal;
    const editedCreditMemoPayableAmount: number = editedDocument.payableAmount;
    const editedDifference: number = roundTo2Decimals(
      originalPayableAmount - editedCreditMemoPayableAmount,
    );
    const editedCreditMemoSubTotal: number = roundTo2Decimals(
      originalCreditMemoSubTotal - editedDifference,
    );

    // take the original subtotal (and surcharge fee) and minus out the edited credit memo subtotal and validate
    const editedTotalAmountToPay: number = roundTo2Decimals(
      subTotal + editedCreditMemoSubTotal,
    );

    const invoices = originalDocuments.filter((d) => !d.isCreditMemo);
    let originalInvoicePayableAmountTotal = 0;

    if (invoices.length > 0) {
      originalInvoicePayableAmountTotal = invoices
        .map((d) =>
          d.isFullInvoiceDispute ? d.payableAmount : d.originalPayableAmount,
        )
        .reduce((a, b) => a + b);
    }

    const errors: ValidateCreditMemoErrors = {
      isNegativePayment: editedTotalAmountToPay < 0,
      isZeroDollarPayment:
        !isZeroDollarPaymentEnabled && editedTotalAmountToPay === 0,
      isCreditUsedForOverPayment:
        Math.abs(editedCreditMemoSubTotal) > originalInvoicePayableAmountTotal,
    };

    return errors;
  }

  private calculateZeroDollarPaymentsUsingCreditMemos(
    sessionDocInfo: SessionDocumentInfo[],
    paymentPreview: PaymentPreviewV1Response,
    isZeroDollarPaymentEnabled: boolean,
    modifiedDocumentBurns: ModifiedDocumentMeta[],
  ): CreditAppliedActionPayload {
    let calculated = false;
    const actionResult: CreditAppliedActionPayload = {
      updatedSessionDocuments: [...sessionDocInfo],
      modifiedDocumentBurns: [],
    };

    paymentPreview.payments.forEach((payment) => {
      const payableBurns = payment.payment.payables.map((p) => p.burn);
      const documents = sessionDocInfo.filter((document) =>
        payableBurns.includes(document.urn),
      );
      const subTotal = this.paymentDocumentService.getSubTotal(documents);
      const creditTotal =
        this.paymentDocumentService.getCreditMemoSubTotal(documents);

      const result = this.calculateZeroDollarPaymentUsingCreditMemos(
        creditTotal,
        subTotal,
        documents,
        isZeroDollarPaymentEnabled,
        modifiedDocumentBurns,
      );

      if (result) {
        calculated = true;
        actionResult.modifiedDocumentBurns =
          actionResult.modifiedDocumentBurns.concat(
            result.modifiedDocumentBurns,
          );
      }
    });

    if (!calculated) {
      return null;
    }

    return actionResult;
  }

  private calculateZeroDollarPaymentUsingCreditMemos(
    creditMemoSubTotal: number,
    subTotal: number,
    sessionDocInfo: SessionDocumentInfo[],
    isZeroDollarPaymentEnabled: boolean,
    modifiedDocumentBurns: ModifiedDocumentMeta[],
  ): CreditAppliedActionPayload {
    const sortedCreditMemos = sessionDocInfo.filter((d) => d.isCreditMemo);

    if (
      sortedCreditMemos.length === 0 ||
      Math.abs(creditMemoSubTotal) < subTotal
    ) {
      return null;
    }
    // if the total amount of credit memos is greater than or equal to the subtotal then we will begin applying the credit towards the the sub-total
    // open balance allocated starts at zero and is used to determine the threshold for when the subtotal is reached
    let openBalanceAllocated = 0;
    const updatedModifiedDocumentBurns: ModifiedDocumentMeta[] =
      modifiedDocumentBurns ?? [];

    sortedCreditMemos.forEach((creditMemoDocData) => {
      // if the open balance that has been allocated thus far is still less than the subtotal then we need to apply more credit to being to equal the subtotal to make a zero dollar payment
      if (Math.abs(openBalanceAllocated) < subTotal) {
        // keep adding the open balance of the credit memos up until we hit the subtotal
        openBalanceAllocated += creditMemoDocData.openBalance;
        // if we have hit the subtotal then we need to either use all or a partial amount of the current open balance for the credit memo
        if (Math.abs(openBalanceAllocated) >= subTotal) {
          this.tagAsZeroDollarAdjustment(
            creditMemoDocData,
            updatedModifiedDocumentBurns,
          );
          // find the difference (eg., subtotal = 5, openBalanceAllocated = -6 then 5 + (-6) = -1)
          const difference = subTotal + openBalanceAllocated;
          // now in this case we want to extract the partial amount to be used for this credit memos payable amount as in the above example the credit memo is greater than the subtotal
          // eg., payable amount = -6 + 1 = -5 (we only need to use -5 credits as the subtotal is 5)
          // eslint-disable-next-line no-param-reassign
          creditMemoDocData.payableAmount = +(
            creditMemoDocData.openBalance + Math.abs(difference)
          ).toFixed(2);
          // if zero dollar payments is off then we need to add one penny from the last credit memo processed so we have a Total Payment Amount of .01 cents (we do not want to zero out)
          if (!isZeroDollarPaymentEnabled) {
            // eslint-disable-next-line no-param-reassign
            creditMemoDocData.payableAmount = +(
              creditMemoDocData.payableAmount + 0.01
            ).toFixed(2);
          }
        }
      } else {
        this.tagAsZeroDollarAdjustment(
          creditMemoDocData,
          updatedModifiedDocumentBurns,
        );

        // if we have reached the subtotal then we need to assign all payable amounts for remaining credit memos that went unused to zero
        // eslint-disable-next-line no-param-reassign
        creditMemoDocData.payableAmount = 0;
      }
    });

    const actionResult: CreditAppliedActionPayload = {
      modifiedDocumentBurns: updatedModifiedDocumentBurns,
      // merge both credit memos and non-credit memos back together.
      updatedSessionDocuments: sessionDocInfo
        .filter((x) => !x.isCreditMemo)
        .concat(sortedCreditMemos),
    };

    return actionResult;
  }

  // (re)tags this urn as system-adjusted, replacing any stale entry (e.g. a
  // prior manual edit) so the tag always reflects who last touched
  // payableAmount. If the value being overwritten was a genuine buyer edit
  // (tagged userEdit), snapshot it first so it can be restored later instead
  // of originalPayableAmount.
  private tagAsZeroDollarAdjustment(
    creditMemoDocData: SessionDocumentInfo,
    modifiedDocumentBurns: ModifiedDocumentMeta[],
  ): void {
    const existingIndex = modifiedDocumentBurns.findIndex(
      (x) => x.urn === creditMemoDocData.urn,
    );
    if (existingIndex !== -1) {
      if (modifiedDocumentBurns[existingIndex].source === 'userEdit') {
        // eslint-disable-next-line no-param-reassign
        creditMemoDocData.preAdjustmentPayableAmount =
          creditMemoDocData.payableAmount;
      }
      modifiedDocumentBurns.splice(existingIndex, 1);
    }
    modifiedDocumentBurns.push({
      urn: creditMemoDocData.urn,
      source: 'zeroDollarAdjustment',
    });
  }
}
